import { getApiData, postData } from 'lib/api/utils';

/** The new sign-in's API (coach API, docs: signin-contract). */

export type TOutcome =
  | 'signed-in' | 'code-required' | 'wrong-password' | 'paused' | 'account-paused'
  | 'client-account' | 'not-a-coach' | 'link-expired' | 'wrong-code' | 'code-expired';

export type TSignInResult = {
  outcome: TOutcome;
  message: string | null;
  triesLeft: number | null;
  pausedUntil: string | null;
  email: string | null;
  coachName: string | null;
  isTeamMember: boolean;
  passwordSaved: boolean;
  challengeId: string | null;
  codeSentTo: string | null;
  token: string;
  refreshToken: string;
  userId: string;
  roles: string[];
  isTemporaryPasswordChanged: boolean;
  isTermsAccepted: boolean;
  isRestrictedAccess: boolean;
  deviceToken: string | null;
  keepSignedIn: boolean;
  sessionExpiresOn: string | null;
};

/** postData's answer: the envelope, or the error message as a string. */
export type TAnswer<T> = { item?: T; success?: boolean } | string;

export type TLink = {
  valid: boolean;
  reason: 'expired' | 'used' | 'not-found' | null;
  purpose: 'reset' | 'coach-invite' | 'team-invite' | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  inviterName: string | null;
  role: string | null;
  areas: string[] | null;
  clientsLimit: number | null;
};

export const SOMETHING_WRONG = 'Something went wrong. Try again in a moment.';

/** The item of an answer, or null for an error. */
export const itemOf = <T>(answer: TAnswer<T> | undefined | null): T | null =>
  answer && typeof answer === 'object' && answer.item ? answer.item : null;

/** The text to show for an answer that isn't a result. */
export const messageOf = (answer: unknown): string =>
  typeof answer === 'string' && answer.trim() ? answer : SOMETHING_WRONG;

export const signIn = (data: { email: string; password: string; keepSignedIn: boolean; deviceToken: string | null }) =>
  postData('Account/sign-in', data) as Promise<TAnswer<TSignInResult>>;

export const verifyCode = (data: { challengeId: string; code: string; rememberDevice: boolean; keepSignedIn: boolean }) =>
  postData('Account/sign-in/verify-code', data) as Promise<TAnswer<TSignInResult>>;

export const resendCode = (challengeId: string) =>
  postData('Account/sign-in/resend-code', { challengeId }) as Promise<TAnswer<TSignInResult>>;

export const forgotPassword = (email: string) =>
  postData('Account/sign-in/forgot', { email }) as Promise<TAnswer<Record<string, never>>>;

export const readLink = (token: string) =>
  getApiData(`Account/sign-in/link?token=${encodeURIComponent(token)}`) as Promise<TAnswer<TLink>>;

export const resetWithLink = (data: { token: string; newPassword: string; keepSignedIn: boolean }) =>
  postData('Account/sign-in/reset', data) as Promise<TAnswer<TSignInResult>>;

/** The apps' older reset links (?code=&email=) still save through the current endpoint. */
export const resetWithCode = (data: { email: string; code: string; newPassword: string }) =>
  postData('Account/reset-password', data) as Promise<TAnswer<unknown>>;

export const acceptInvite = (data: { token: string; firstName: string; lastName: string; newPassword: string; acceptTerms: boolean }) =>
  postData('Account/sign-in/accept-invite', data) as Promise<TAnswer<TSignInResult>>;
